import React, { useEffect, useState } from 'react';
import axios from 'axios';
import AsyncSelect from 'react-select/async';
import FilterDrawer from '../../../shared/FilterDrawer';
import CancelButton from '../../../shared/CancelButton';
import RequiredMark from '../../../shared/RequiredMark';
import FieldHelp from '../../../shared/FieldHelp';
import '../../../shared/StyledInput.css';

// Matches styled-input's 34px height (react-select can't inherit that
// chrome via className).
const entitySelectStyles = {
  container: (base) => ({ ...base, width: '100%' }),
  control: (base) => ({
    ...base,
    minHeight: 34,
    height: 34,
    borderRadius: 4,
    borderColor: '#a9a9a9',
    boxShadow: 'none',
    fontSize: 15,
    '&:hover': { borderColor: '#a9a9a9' },
  }),
  valueContainer: (base) => ({ ...base, height: 32, padding: '0 8px' }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  indicatorsContainer: (base) => ({ ...base, height: 32 }),
  indicatorSeparator: () => ({ display: 'none' }),
  option: (base) => ({ ...base, fontSize: 14 }),
};

// Plain-text pieces of the "SoundBay is a [role] of [company]" sentence.
const sentenceStyle = { fontSize: 14, color: '#333', display: 'block', marginBottom: 4 };

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };

// Add Person / Add Company — one drawer for both, driven by `category`
// ('individual' | 'organization'), opened from the People/Companies cards'
// kebab on CommercialEntityDetailPage.js. Links an existing CommercialEntity
// found via quick-search, or creates a new one inline (mirrors the Suburb
// Autocomplete's search-or-create fallback on that same page), always
// tagged with a Link Role from the tenant's Link Role Types.
//
// Both drawers read as a sentence, and a person is always its subject, so
// People roles only ever show under their own name ("Jane is a Decision
// Maker of Acme") and never need an inverse_name to make sense:
//   - Add Person: "[person] is a [role] of <this record>" — forward roles.
//   - Add Company from a person's page: "<Jane> is a [role] of [company]" —
//     People roles saved reversed (see addCommercialEntityLink's `reverse`).
//   - Add Company from a company's page: "<SoundBay> is a [role] of
//     [company]" — both ends of each Organisation role, as this record's
//     side (a forward role's inverse_name, a reversed role's name).
function AddLinkDrawer({ open, onOpenChange, category, ownerEntityId, ownerEntityType, ownerEntityName, onAdded }) {
  const [linkRoleTypes, setLinkRoleTypes] = useState([]);
  // Option value: "<id>" for a forward role, "r:<id>" for a reversed one.
  const [linkRoleTypeId, setLinkRoleTypeId] = useState('');
  const [creatingNew, setCreatingNew] = useState(false);
  const [selectedOption, setSelectedOption] = useState(null);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setLinkRoleTypeId('');
    setCreatingNew(false);
    setSelectedOption(null);
    setNewFirstName('');
    setNewLastName('');
    setNewName('');
    setError('');
  }, [open, category]);

  useEffect(() => {
    if (!open || !category) return;
    axios.get('/api/link-role-types', { params: { status: 'active' } })
      .then((res) => setLinkRoleTypes(res.data.data || []))
      .catch(() => setLinkRoleTypes([]));
  }, [open, category]);

  const isIndividual = category === 'individual';
  const nounLower = isIndividual ? 'Individual' : 'Organisation';

  // Person first when adding a person; otherwise this record comes first.
  const addedFirst = isIndividual;
  const ownerIsPerson = ownerEntityType === 'individual';
  const inverseLabel = (t) => t.inverse_name || `Reverse of ${t.name}`;
  const isSymmetric = (t) => !!t.inverse_name && t.inverse_name.trim().toLowerCase() === t.name.trim().toLowerCase();
  let roleOptions;
  if (addedFirst) {
    roleOptions = linkRoleTypes
      .filter((t) => t.category === 'individual')
      .map((t) => ({ value: String(t.id), label: t.name }));
  } else if (ownerIsPerson) {
    roleOptions = linkRoleTypes
      .filter((t) => t.category === 'individual')
      .map((t) => ({ value: `r:${t.id}`, label: t.name }));
  } else {
    // A symmetric role (e.g. "Collaboration") would just repeat its
    // forward option, so its reversed copy is left out.
    roleOptions = [
      ...linkRoleTypes
        .filter((t) => t.category === 'organization')
        .map((t) => ({ value: String(t.id), label: inverseLabel(t) })),
      ...linkRoleTypes
        .filter((t) => t.category === 'organization' && !isSymmetric(t))
        .map((t) => ({ value: `r:${t.id}`, label: t.name })),
    ];
  }
  roleOptions.sort((a, b) => a.label.localeCompare(b.label));
  const selectedRoleLabel = roleOptions.find((o) => o.value === linkRoleTypeId)?.label || '';
  const article = /^[aeiou]/i.test(selectedRoleLabel) ? 'an' : 'a';

  const loadEntityOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 1) return callback([]);
    axios.get('/api/commercial-entities/quick-search', { params: { q: inputValue, type: category, excludeId: ownerEntityId } })
      .then((res) => callback((res.data.data || []).map((r) => ({ value: r.id, label: r.name }))))
      .catch(() => callback([]));
  };

  const canSave = !!linkRoleTypeId && (creatingNew
    ? (isIndividual ? newFirstName.trim() && newLastName.trim() : newName.trim())
    : !!selectedOption);

  const handleSave = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const isReverse = linkRoleTypeId.startsWith('r:');
      const payload = { link_role_type_id: isReverse ? linkRoleTypeId.slice(2) : linkRoleTypeId };
      if (isReverse) {
        payload.reverse = true;
        payload.type = category;
      }
      if (creatingNew) {
        if (isIndividual) {
          payload.first_name = newFirstName.trim();
          payload.last_name = newLastName.trim();
        } else {
          payload.name = newName.trim();
        }
      } else {
        payload.related_entity_id = selectedOption.value;
      }
      const res = await axios.post(`/api/commercial-entities/${ownerEntityId}/links`, payload);
      onAdded(res.data.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Save failed');
    }
    setSaving(false);
  };

  const roleSelect = (
    <select
      className="styled-input"
      value={linkRoleTypeId}
      onChange={(e) => setLinkRoleTypeId(e.target.value)}
      style={{ width: '100%', fontSize: 14 }}
      aria-label="Link Role"
    >
      <option value="">Select a role...</option>
      {roleOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );

  const entityField = (
    <div>
      <label style={addedFirst ? fieldLabelStyle : sentenceStyle}>
        {addedFirst ? nounLower : 'of'}{' '}
        <button
          type="button"
          onClick={() => { setCreatingNew((v) => !v); setSelectedOption(null); setError(''); }}
          style={{ background: 'none', border: 'none', color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', fontSize: 11, padding: 0, textTransform: 'none', letterSpacing: 0 }}
        >
          {creatingNew ? 'Cancel' : `+ Add new ${nounLower}`}
        </button>
      </label>
      {creatingNew ? (
        isIndividual ? (
          <>
            <input
              className="styled-input"
              value={newFirstName}
              onChange={(e) => setNewFirstName(e.target.value)}
              placeholder="First Name"
              style={{ width: '100%', fontSize: 14, marginBottom: 8 }}
            />
            <input
              className="styled-input"
              value={newLastName}
              onChange={(e) => setNewLastName(e.target.value)}
              placeholder="Last Name"
              style={{ width: '100%', fontSize: 14 }}
            />
          </>
        ) : (
          <input
            className="styled-input"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Company Name"
            style={{ width: '100%', fontSize: 14 }}
          />
        )
      ) : (
        <AsyncSelect
          cacheOptions
          defaultOptions={false}
          loadOptions={loadEntityOptions}
          value={selectedOption}
          onChange={setSelectedOption}
          placeholder={`Search ${nounLower.toLowerCase()}s...`}
          isClearable
          styles={entitySelectStyles}
          noOptionsMessage={() => `No ${nounLower.toLowerCase()}s found`}
          loadingMessage={() => 'Searching...'}
          aria-label={nounLower}
        />
      )}
    </div>
  );

  return (
    <FilterDrawer
      open={open}
      onOpenChange={onOpenChange}
      title={isIndividual ? 'Add Person' : 'Add Company'}
      description={`Link an existing ${nounLower}, or create a new one.`}
      footer={(
        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          <CancelButton onClick={() => onOpenChange(false)} disabled={saving} />
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || !canSave}
            style={{ padding: '8px 14px', border: 'none', color: '#fff', background: '#1976d2', borderRadius: 6, cursor: (saving || !canSave) ? 'not-allowed' : 'pointer', fontSize: 13, opacity: (saving || !canSave) ? 0.6 : 1 }}
          >
            Save
          </button>
        </div>
      )}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, marginBottom: 8 }}>
        <label style={{ ...fieldLabelStyle, marginBottom: 0 }}>Link Role<RequiredMark /></label>
        <FieldHelp
          fieldKey={`commercial-entities.add-link-role-${category}`}
          pageKey="commercial-entities"
          articleSlug="entities"
          articleTitle="Commercial Entities"
          label={isIndividual ? 'Add Person — Link Role' : 'Add Company — Link Role'}
        >
          {isIndividual
            ? 'Complete the sentence with what the person is to the record you\'re on. E.g. on Acme\'s page, "Jane is a Decision Maker of Acme". The link shows on both records, each described from its own side.'
            : 'Complete the sentence with what the record you\'re on is to the company you\'re adding. E.g. on SoundBay\'s page, "SoundBay is a Chain of Bob\'s Hifi" makes Bob\'s Hifi a Store in SoundBay\'s chain; on Jane\'s page, "Jane is an Employee of Acme". The link shows on both records, each described from its own side.'}
        </FieldHelp>
      </span>

      {addedFirst ? (
        <>
          {entityField}
          <div style={{ ...sentenceStyle, marginTop: 10 }}>is {article}</div>
          {roleSelect}
          <div style={{ ...sentenceStyle, marginTop: 4 }}>of {ownerEntityName}</div>
        </>
      ) : (
        <>
          <div style={sentenceStyle}>{ownerEntityName} is {article}</div>
          {roleSelect}
          <div style={{ marginTop: 4 }}>{entityField}</div>
        </>
      )}

      {error && <div style={{ color: 'red', fontSize: 13, marginTop: 8 }}>{error}</div>}
    </FilterDrawer>
  );
}

export default AddLinkDrawer;
